// types
import {
  AppointmentFormFieldData,
  AppointmentFormFieldEntity
} from 'components/mrp/components/AppointmentForm/AppointmentForm.types';
import { MrpEntityKey } from '../../../EntitySection/EntitySection.types';
import { PlanningEntityType } from 'generated/index';

// components
import { Autocomplete } from 'components/shared/index';

// hooks
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// libs
import { queries } from 'RestAPIs/rapini';

// stores
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useShallow } from 'zustand/shallow';

const WorkplaceSelect = () => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const { control, setValue, getValues } =
    useFormContext<AppointmentFormFieldData>();
  const formKey = `planEntities.WORKPLACE` as MrpEntityKey;

  const { entityIdsPerCalendarId, openCalendar, closeCalendar } =
    useCalendarStore(
      useShallow((state) => ({
        entityIdsPerCalendarId: state.entityIdsPerCalendarId,
        openCalendar: state.control.openCalendar,
        closeCalendar: state.control.closeCalendar
      }))
    );

  const setWorkplace = (workplaceEntity: AppointmentFormFieldEntity | null) => {
    const previousValue = getValues(formKey)[0] || null;
    if (!!previousValue) {
      const calendarId =
        Object.entries(entityIdsPerCalendarId)
          .filter(
            ({ '1': values }) =>
              values.length == 1 &&
              values.indexOf(previousValue.entityId!) != -1
          )
          .map(({ '0': calendarId }) => calendarId)[0] || null;

      if (calendarId) {
        closeCalendar(calendarId);
      }
    }

    setValue(formKey, workplaceEntity ? [workplaceEntity] : [], {
      shouldValidate: true,
      shouldDirty: true
    });

    if (!!workplaceEntity) {
      openCalendar([
        {
          label: workplaceEntity.label,
          planningEntityIdentifier: {
            planningEntityType: 'WORKPLACE',
            entityId: workplaceEntity.entityId
          }
        }
      ]);
    }
  };

  const { data = [] } = queries.useFindPlanningEntities(
    '',
    // TODO: BE needs to fix interface. BE expects a comma separated string, not an array
    ['WORKPLACE'].join(',') as unknown as PlanningEntityType[],
    100,
    //@ts-expect-error
    ['MRP'].join(',')
  );

  const entity = useWatch({ name: formKey, control })?.[0];

  const options = useMemo(
    () =>
      data.map(({ id, label, type }) => ({
        label,
        planningEntityType: type,
        entityId: id
      })) as AppointmentFormFieldEntity[],
    [data]
  );

  const workPlace = useMemo(() => {
    if (!entity) return null;

    return options.find((o) => o.entityId === entity.entityId) || null;
  }, [entity, options]);

  return (
    <Autocomplete
      idProperty="entityId"
      label={t(`entityTypeMap.WORKPLACE.single`)}
      onSelected={setWorkplace}
      options={options || []}
      value={workPlace}
      fullWidth
      variant="outlined"
    />
  );
};

export default WorkplaceSelect;
